<template>
  <div class="p-6 max-w-7xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="qts-headline-2 text-white">Shipping Operations</h1>
        <p class="qts-body-2 text-slate-400 mt-1">Manage shipments, refunds, and problem tickets</p>
      </div>
      <button
        @click="loadAll"
        :disabled="loading"
        class="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm hover:bg-slate-700 transition-colors"
      >
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="md3-card-container p-4">
        <p class="qts-label-2 text-slate-400 uppercase tracking-wide">Active Shipments</p>
        <p class="qts-headline-3 text-white mt-1">{{ summary.active }}</p>
      </div>
      <div class="md3-card-container p-4">
        <p class="qts-label-2 text-slate-400 uppercase tracking-wide">Outbound Spend</p>
        <p class="qts-headline-3 text-white mt-1">${{ (summary.outboundSpendCents / 100).toFixed(2) }}</p>
        <p v-if="summary.returnEstCents > 0" class="text-[10px] text-slate-500 mt-0.5">
          + ${{ (summary.returnEstCents / 100).toFixed(2) }} est. returns (pay-on-use)
        </p>
      </div>
      <div class="md3-card-container p-4">
        <p class="qts-label-2 text-slate-400 uppercase tracking-wide">Pending Refunds</p>
        <p class="qts-headline-3 text-amber-400 mt-1">{{ summary.pendingRefunds }}</p>
      </div>
      <div class="md3-card-container p-4">
        <p class="qts-label-2 text-slate-400 uppercase tracking-wide">Refunded</p>
        <p class="qts-headline-3 text-green-400 mt-1">${{ (summary.refundedCents / 100).toFixed(2) }}</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 mb-4">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="activeTab = tab.key"
        :class="[
          'px-4 py-2 rounded-t text-sm transition-colors',
          activeTab === tab.key
            ? 'bg-slate-800 text-white border-b-2 border-primary-500'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/50',
        ]"
      >
        {{ tab.label }}
        <span v-if="tab.count" class="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-slate-700">{{ tab.count }}</span>
      </button>
    </div>

    <!-- Shipments Table -->
    <div v-if="activeTab === 'shipments'" class="md3-card-container overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="md3-table-header">
            <tr>
              <th class="text-left p-3">Tracking</th>
              <th class="text-left p-3">Type</th>
              <th class="text-left p-3">Carrier</th>
              <th class="text-left p-3">Status</th>
              <th class="text-left p-3">Cost</th>
              <th class="text-left p-3">Created</th>
              <th class="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!shipments.length" class="border-t border-slate-700/50">
              <td colspan="7" class="p-4 text-center text-slate-500">No shipments found</td>
            </tr>
            <tr
              v-for="s in shipments"
              :key="s.id"
              class="border-t border-slate-700/50 hover:bg-slate-800/30 transition-colors"
            >
              <td class="p-3 font-mono text-xs text-slate-300">{{ s.tracking_number || '—' }}</td>
              <td class="p-3">
                <span
                  :class="[
                    'px-2 py-0.5 rounded text-[10px] uppercase font-medium',
                    s.type === 'outbound' ? 'bg-blue-900/40 text-blue-400' : 'bg-amber-900/40 text-amber-400',
                  ]"
                >{{ s.type }}</span>
              </td>
              <td class="p-3 text-slate-300">{{ s.carrier }} {{ s.service }}</td>
              <td class="p-3">
                <span :class="statusBadgeClass(s.status)">{{ statusLabel(s.status) }}</span>
              </td>
              <td class="p-3 text-slate-300">
                <template v-if="s.type === 'return'">
                  <span v-if="s.cost_cents" class="flex items-center gap-1.5">
                    ${{ (s.cost_cents / 100).toFixed(2) }}
                    <span class="text-[10px] px-1.5 py-0.5 rounded bg-green-900/40 text-green-400">Pay-on-Use</span>
                  </span>
                  <span v-else class="text-[10px] text-green-500">Pay-on-Use</span>
                  <span class="text-[9px] text-slate-500 block mt-0.5">
                    {{ s.cost_cents ? 'Est. cost — charged only when scanned' : 'No charge until carrier scans label' }}
                  </span>
                </template>
                <template v-else>
                  {{ s.cost_cents ? '$' + (s.cost_cents / 100).toFixed(2) : '—' }}
                </template>
              </td>
              <td class="p-3 text-slate-500 text-xs">{{ formatDate(s.created_at) }}</td>
              <td class="p-3">
                <div class="flex items-center gap-2">
                  <button
                    v-if="canRefund(s)"
                    @click="requestRefund(s)"
                    :disabled="refundingId === s.id"
                    class="px-2 py-1 rounded text-[11px] bg-red-900/30 text-red-400 border border-red-800/50 hover:bg-red-900/50 transition-colors"
                  >
                    {{ refundingId === s.id ? 'Requesting…' : 'Cancel / Refund' }}
                  </button>
                  <button
                    @click="openIssue(s)"
                    class="px-2 py-1 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-colors"
                  >
                    Report Problem
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Refunds Table -->
    <div v-if="activeTab === 'refunds'" class="md3-card-container overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="md3-table-header">
            <tr>
              <th class="text-left p-3">Refund #</th>
              <th class="text-left p-3">Shipment</th>
              <th class="text-left p-3">Status</th>
              <th class="text-left p-3">Amount</th>
              <th class="text-left p-3">Requested</th>
              <th class="text-left p-3">Resolved</th>
              <th class="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!refunds.length" class="border-t border-slate-700/50">
              <td colspan="7" class="p-4 text-center text-slate-500">No refund requests</td>
            </tr>
            <tr
              v-for="r in refunds"
              :key="r.id"
              class="border-t border-slate-700/50 hover:bg-slate-800/30 transition-colors"
            >
              <td class="p-3 text-slate-300">#{{ r.id }}</td>
              <td class="p-3 font-mono text-xs text-slate-400">
                {{ r.shipment?.tracking_number || '—' }}
                <span class="text-[10px] text-slate-500 block">{{ r.shipment?.carrier }} {{ r.shipment?.type }}</span>
              </td>
              <td class="p-3">
                <span :class="refundBadgeClass(r.status)">{{ r.status }}</span>
              </td>
              <td class="p-3 text-slate-300">{{ r.amount_cents ? '$' + (r.amount_cents / 100).toFixed(2) : '—' }}</td>
              <td class="p-3 text-slate-500 text-xs">{{ formatDate(r.requested_at) }}</td>
              <td class="p-3 text-slate-500 text-xs">{{ r.resolved_at ? formatDate(r.resolved_at) : '—' }}</td>
              <td class="p-3">
                <button
                  v-if="r.status === 'queued' || r.status === 'pending'"
                  @click="checkRefund(r)"
                  :disabled="checkingRefundId === r.id"
                  class="px-2 py-1 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-colors"
                >
                  {{ checkingRefundId === r.id ? 'Checking…' : 'Check Status' }}
                </button>
                <span v-if="r.error_message" class="text-[10px] text-red-400 block mt-1">{{ r.error_message }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Issues Table -->
    <div v-if="activeTab === 'issues'" class="md3-card-container overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="md3-table-header">
            <tr>
              <th class="text-left p-3">Issue #</th>
              <th class="text-left p-3">Type</th>
              <th class="text-left p-3">Status</th>
              <th class="text-left p-3">Description</th>
              <th class="text-left p-3">External Claim</th>
              <th class="text-left p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!issues.length" class="border-t border-slate-700/50">
              <td colspan="6" class="p-4 text-center text-slate-500">No issues reported</td>
            </tr>
            <tr
              v-for="issue in issues"
              :key="issue.id"
              class="border-t border-slate-700/50 hover:bg-slate-800/30 transition-colors"
            >
              <td class="p-3 text-slate-300">#{{ issue.id }}</td>
              <td class="p-3">
                <span class="px-2 py-0.5 rounded text-[10px] uppercase bg-slate-700 text-slate-300">{{ issue.issue_type }}</span>
              </td>
              <td class="p-3">
                <span :class="issueBadgeClass(issue.status)">{{ issue.status }}</span>
              </td>
              <td class="p-3 text-slate-400 text-xs max-w-[300px] truncate">{{ issue.description || '—' }}</td>
              <td class="p-3 text-xs">
                <a v-if="issue.external_claim_url" :href="issue.external_claim_url" target="_blank" class="text-primary-400 underline">
                  {{ issue.external_claim_id || 'View Claim' }}
                </a>
                <span v-else class="text-slate-500">—</span>
              </td>
              <td class="p-3 text-slate-500 text-xs">{{ formatDate(issue.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Report Problem Modal -->
    <teleport to="body">
      <div v-if="issueModal" class="fixed inset-0 z-[1060] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div class="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-lg mx-4">
          <h3 class="qts-title-1 text-white mb-4">Report Shipment Problem</h3>
          <p class="text-slate-400 text-xs mb-4">
            Tracking: <span class="font-mono text-white">{{ issueModal.tracking_number }}</span>
            — {{ issueModal.carrier }} {{ issueModal.type }}
          </p>

          <div class="space-y-3">
            <div>
              <label class="qts-label-2 text-slate-300 block mb-1">Issue Type *</label>
              <select v-model="issueForm.type" class="w-full md3-input bg-slate-800 text-white border-slate-700">
                <option value="lost">Lost Package</option>
                <option value="damaged">Damaged Package</option>
                <option value="delayed">Excessive Delay</option>
                <option value="wrong_address">Wrong Address</option>
                <option value="carrier_error">Carrier Error</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label class="qts-label-2 text-slate-300 block mb-1">Description</label>
              <textarea
                v-model="issueForm.description"
                rows="3"
                class="w-full md3-input bg-slate-800 text-white border-slate-700"
                placeholder="Describe the issue…"
              ></textarea>
            </div>
            <div>
              <label class="qts-label-2 text-slate-300 block mb-1">External Claim ID</label>
              <input
                v-model="issueForm.externalClaimId"
                class="w-full md3-input bg-slate-800 text-white border-slate-700"
                placeholder="XCover or carrier claim number (optional)"
              />
            </div>
            <div>
              <label class="qts-label-2 text-slate-300 block mb-1">Claim URL</label>
              <input
                v-model="issueForm.externalClaimUrl"
                class="w-full md3-input bg-slate-800 text-white border-slate-700"
                placeholder="Link to external claim (optional)"
              />
            </div>
          </div>

          <div class="mt-2 p-3 rounded bg-slate-800/50 border border-slate-700/50">
            <p class="text-[11px] text-slate-400">
              <strong class="text-slate-300">Note:</strong> Shippo does not provide a claims API.
              For insured packages, file claims at
              <a href="https://www.xcover.com" target="_blank" class="text-primary-400 underline">xcover.com</a>.
              For uninsured packages, contact the carrier directly.
            </p>
          </div>

          <div class="flex gap-3 mt-4">
            <button
              @click="issueModal = null"
              class="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm"
            >Cancel</button>
            <button
              @click="submitIssue"
              :disabled="issueSaving"
              class="px-3 py-2 rounded btn-primary text-sm"
            >{{ issueSaving ? 'Saving…' : 'Submit Issue' }}</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import { createLogger } from '@/lib/logger'

const log = createLogger('AdminShipping')

const loading = ref(false)
const activeTab = ref<'shipments' | 'refunds' | 'issues'>('shipments')
const shipments = ref<any[]>([])
const refunds = ref<any[]>([])
const issues = ref<any[]>([])
const refundingId = ref<number | null>(null)
const checkingRefundId = ref<number | null>(null)

const tabs = computed(() => [
  { key: 'shipments' as const, label: 'Shipments', count: shipments.value.length },
  { key: 'refunds' as const, label: 'Refunds', count: refunds.value.length },
  { key: 'issues' as const, label: 'Issues', count: issues.value.filter((i: any) => i.status !== 'resolved' && i.status !== 'closed').length },
])

const summary = computed(() => {
  const active = shipments.value.filter((s: any) => !['cancelled', 'received', 'checked_in'].includes(s.status)).length
  const outboundSpendCents = shipments.value
    .filter((s: any) => s.type === 'outbound')
    .reduce((sum: number, s: any) => sum + (s.cost_cents || 0), 0)
  const returnEstCents = shipments.value
    .filter((s: any) => s.type === 'return')
    .reduce((sum: number, s: any) => sum + (s.cost_cents || 0), 0)
  const pendingRefunds = refunds.value.filter((r: any) => r.status === 'queued' || r.status === 'pending').length
  const refundedCents = refunds.value
    .filter((r: any) => r.status === 'success')
    .reduce((sum: number, r: any) => sum + (r.amount_cents || 0), 0)
  return { active, outboundSpendCents, returnEstCents, pendingRefunds, refundedCents }
})

// Issue modal state
const issueModal = ref<any>(null)
const issueSaving = ref(false)
const issueForm = reactive({
  type: 'other' as string,
  description: '',
  externalClaimId: '',
  externalClaimUrl: '',
})

onMounted(() => loadAll())

async function loadAll() {
  loading.value = true
  try {
    await Promise.all([loadShipments(), loadRefunds(), loadIssues()])
  } finally {
    loading.value = false
  }
}

async function loadShipments() {
  const { data, error } = await supabase
    .schema('app' as any)
    .from('shipments')
    .select('id, type, carrier, service, tracking_number, status, cost_cents, currency, created_at, metadata')
    .order('created_at', { ascending: false })
    .limit(200)
  if (error) log.error('Failed to load shipments:', error.message)
  shipments.value = data || []
}

async function loadRefunds() {
  const { data, error } = await supabase.functions.invoke('shipping-refund', {
    body: { action: 'list' },
  })
  if (data?.refunds) refunds.value = data.refunds
  else if (error) log.error('Failed to load refunds:', error)
}

async function loadIssues() {
  const { data, error } = await supabase
    .schema('app' as any)
    .from('shipment_issues')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) log.error('Failed to load issues:', error.message)
  issues.value = data || []
}

function canRefund(s: any): boolean {
  // Can refund if label_purchased or outbound_label and not already refunded/cancelled
  const refundable = ['label_purchased', 'outbound_label', 'return_initiated', 'created']
  if (!refundable.includes(s.status)) return false
  // Check if a refund already exists for this shipment
  return !refunds.value.some((r: any) => r.shipment_id === s.id)
}

async function requestRefund(s: any) {
  refundingId.value = s.id
  try {
    const { data, error } = await supabase.functions.invoke('shipping-refund', {
      body: { action: 'refund', shipmentId: s.id },
    })
    if (error) throw error
    if (data?.ok === false) {
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'error', message: data.errorMessage || data.error || 'Refund failed' } }))
    } else {
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: `Refund requested (${data?.refundStatus || 'queued'})` } }))
      await loadAll()
    }
  } catch (e: any) {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'error', message: e?.message || 'Refund request failed' } }))
  } finally {
    refundingId.value = null
  }
}

async function checkRefund(r: any) {
  checkingRefundId.value = r.id
  try {
    const { data } = await supabase.functions.invoke('shipping-refund', {
      body: { action: 'check', refundId: r.id },
    })
    if (data?.refund) {
      const idx = refunds.value.findIndex((x: any) => x.id === r.id)
      if (idx >= 0) refunds.value[idx].status = data.refund.status
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', message: `Refund status: ${data.refund.status}` } }))
    }
  } finally {
    checkingRefundId.value = null
  }
}

function openIssue(s: any) {
  issueModal.value = s
  issueForm.type = 'other'
  issueForm.description = ''
  issueForm.externalClaimId = ''
  issueForm.externalClaimUrl = ''
}

async function submitIssue() {
  if (!issueModal.value) return
  issueSaving.value = true
  try {
    const { error } = await supabase
      .schema('app' as any)
      .from('shipment_issues')
      .insert({
        shipment_id: issueModal.value.id,
        checkout_id: issueModal.value.checkout_id || null,
        issue_type: issueForm.type,
        description: issueForm.description || null,
        external_claim_id: issueForm.externalClaimId || null,
        external_claim_url: issueForm.externalClaimUrl || null,
        status: 'open',
      })
    if (error) throw error
    window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Issue reported' } }))
    issueModal.value = null
    await loadIssues()
  } catch (e: any) {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'error', message: e?.message || 'Failed to save issue' } }))
  } finally {
    issueSaving.value = false
  }
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    created: 'Created',
    label_purchased: 'Label Purchased',
    outbound_label: 'Outbound Label',
    carrier_accepted: 'Carrier Accepted',
    outbound_in_transit: 'In Transit',
    out_for_delivery: 'Out for Delivery',
    with_borrower: 'Delivered',
    return_initiated: 'Return Label',
    return_carrier_scan: 'Return Pickup',
    return_in_transit: 'Return Transit',
    received: 'Received',
    checked_in: 'Checked In',
    cancelled: 'Cancelled',
  }
  return labels[status] || status
}

function statusBadgeClass(status: string): string {
  const base = 'px-2 py-0.5 rounded text-[10px] uppercase font-medium'
  if (status === 'cancelled') return `${base} bg-red-900/40 text-red-400`
  if (status === 'with_borrower' || status === 'received' || status === 'checked_in') return `${base} bg-green-900/40 text-green-400`
  if (status.includes('transit') || status === 'carrier_accepted' || status === 'out_for_delivery' || status === 'return_carrier_scan') return `${base} bg-blue-900/40 text-blue-400`
  return `${base} bg-slate-700 text-slate-300`
}

function refundBadgeClass(status: string): string {
  const base = 'px-2 py-0.5 rounded text-[10px] uppercase font-medium'
  if (status === 'success') return `${base} bg-green-900/40 text-green-400`
  if (status === 'error') return `${base} bg-red-900/40 text-red-400`
  if (status === 'pending' || status === 'queued') return `${base} bg-amber-900/40 text-amber-400`
  return `${base} bg-slate-700 text-slate-300`
}

function issueBadgeClass(status: string): string {
  const base = 'px-2 py-0.5 rounded text-[10px] uppercase font-medium'
  if (status === 'resolved' || status === 'closed') return `${base} bg-green-900/40 text-green-400`
  if (status === 'claim_filed') return `${base} bg-amber-900/40 text-amber-400`
  if (status === 'open') return `${base} bg-red-900/40 text-red-400`
  return `${base} bg-slate-700 text-slate-300`
}

function formatDate(d: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return d
  }
}
</script>
